import React, { useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useResetPassword } from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import { Link, useParams } from "react-router";
import { PasswordInput, PasswordValidator } from "@/components";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

export const RecoverPasswordForm = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const params = useParams();
  const token = params.code;

  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [infoMessage, setInfoMessage] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [passwordValid, setPasswordValid] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  // Compute saveButtonDisabled based on form validation
  const saveButtonDisabled = !(
    password1 &&
    password2 &&
    passwordValid &&
    password1 === password2
  );

  const { submit, isSubmitting } = useResetPassword(
    () => {
      setErrorMessage([]);
      setInfoMessage([gettext("Your password has been successfully reset.")]);
      setPasswordChanged(true);
    },
    (error: string) => {
      setInfoMessage([]);
      setErrorMessage([error]);
    },
  );

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setInfoMessage([gettext("Saving. Please wait.")]);
    submit({
      password1,
      password2,
      token,
    });
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword1(event.target.value);
    if (password2 && password2 !== event.target.value) {
      setConfirmPasswordError(true);
    } else {
      setConfirmPasswordError(false);
    }
  };

  const handlePasswordIsValid = (isValid: boolean) => {
    setPasswordValid(isValid);
  };

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPassword2(event.target.value);
    if (password1 !== event.target.value) {
      setConfirmPasswordError(true);
    } else {
      setConfirmPasswordError(false);
    }
  };

  return (
    <>
      {!passwordChanged && (
        <Box component="form" onSubmit={handleSubmit}>
          <Grid
            container
            spacing={2}
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <Grid size={{ xs: 12, sm: 8 }} sx={{ textAlign: "left" }}>
              <Typography variant="h2" sx={{ color: "grey.900" }}>
                {gettext("Reset Password")}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }} sx={{ textAlign: "left" }}>
              <Stack>
                <PasswordInput
                  placeholder={gettext("Password")}
                  value={password1}
                  required
                  onChange={handlePasswordChange}
                />
              </Stack>
              <PasswordValidator
                password={password1}
                handleIsValid={handlePasswordIsValid}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }} sx={{ textAlign: "left" }}>
              <Stack>
                <PasswordInput
                  placeholder={gettext("Confirm Password")}
                  value={password2}
                  required
                  onChange={handleConfirmPasswordChange}
                />
              </Stack>
              {confirmPasswordError && (
                <FormHelperText error id="accountId-error">
                  {gettext("Confirm password does not match.")}
                </FormHelperText>
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }} sx={{ textAlign: "left" }}>
              <Button
                disableElevation
                disabled={isSubmitting || saveButtonDisabled}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                sx={{ color: "primary" }}
              >
                {gettext("Reset Password")}
              </Button>
            </Grid>
            {(infoMessage?.length ?? 0) > 0 && (
              <Grid size={{ xs: 12, sm: 8 }} sx={{ textAlign: "left" }}>
                <Alert
                  severity="info"
                  sx={{ whiteSpace: "pre-line", textAlign: "left" }}
                >
                  {infoMessage.join("\n")}
                </Alert>
              </Grid>
            )}
            {(errorMessage?.length ?? 0) > 0 && (
              <Grid size={{ xs: 12, sm: 8 }} sx={{ textAlign: "left" }}>
                <Alert
                  severity="error"
                  sx={{ whiteSpace: "pre-line", textAlign: "left" }}
                >
                  {errorMessage.join("\n")}
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {passwordChanged && (
        <Box>
          <Grid
            container
            spacing={2}
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <Grid size={{ xs: 12, sm: 8 }} sx={{ textAlign: "left" }}>
              <TaskAltIcon color="success" sx={{ fontSize: 60 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Typography
                variant="h2"
                sx={{ textAlign: "left", color: "grey.900" }}
              >
                {gettext("Password Changed")}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }} sx={{ mb: 5 }}>
              <Typography variant="body2" sx={{ textAlign: "left" }}>
                {gettext("Login with your new password right away.")}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }}>
              <Button
                component={Link}
                to={"/auth/login"}
                fullWidth
                size="large"
                variant="contained"
                sx={{ color: "primary" }}
              >
                {gettext("Sign In")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};
